import {
  Box,
  Container,
  Button,
  Stack,
  Typography,
  Paper,
  Alert,
  TextField,
  Chip,
} from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { Feature } from '@/types/mapbox';

import { useInitiateMeetTerri } from '@/api/endpoints/meet-terri/useInitiateMeetTerri';
import { useVendorSearchStream } from '@/api/endpoints/meet-terri/useVendorSearchStream';
// import { useGetSearchHistory } from '@/api/endpoints/vendor/useGetSearchHistory';
import { useSearchVendors } from '@/api/endpoints/vendor/useSearchVendors';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { PhoneXIcon } from '@/assets/icons/PhoneXIcon';
import { SearchLgIcon } from '@/assets/icons/SearchLgIcon';
import { ShieldTickIcon } from '@/assets/icons/ShieldTickIcon';
import { ZapFastIcon } from '@/assets/icons/ZapFastIcon';
import { PublicRoutes, PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { useIPLocationLazy } from '@/hooks/useIPLocation';
import { convertIPLocationToMapboxFeature } from '@/utils/locationToMapboxFeature';

import MeetTerriDialog from '@/components/dialogs/MeetTerriDialog';
import MapboxAddressAutocomplete from '@/components/form/MapboxAddressAutocomplete';
import BaseLottie from '@/components/lottie/BaseLottie';
import HowWeRankVendors from '@/components/vendor/HowWeRankVendors';
// import RecentSearchCard from '@/components/vendor/RecentSearchCard';

import vendorSearchAnimation from '../../../../public/assets/images/vendor_search.json';

const VendorSearchSection: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<Feature | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showMeetTerriDialog, setShowMeetTerriDialog] = useState(false);
  const [meetTerriError, setMeetTerriError] = useState<string | null>(null);
  const [meetTerriSessionToken, setMeetTerriSessionToken] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const searchVendorsMutation = useSearchVendors();
  const initiateMeetTerriMutation = useInitiateMeetTerri();
  const { lastSearchRequest, clearLastRequest } = useVendorSearchStream(meetTerriSessionToken);

  // Get search history for logged-in users - DISABLED
  // const { data: searchHistory } = useGetSearchHistory(!!user);
  // const mostRecentSearch = searchHistory?.[0]; // API returns sorted by most recent

  // IP-based location detection for non-logged-in users
  const ipLocation = useIPLocationLazy({
    onSuccess: data => {
      // Only use IP location if user is not logged in and no address is set
      if (!user && !selectedAddress && !hasInitialized) {
        const feature = convertIPLocationToMapboxFeature(data, 'ip-location');
        setSelectedAddress(feature);
        setHasInitialized(true);
      }
    },
  });

  // Trigger IP location detection for non-logged-in users
  useEffect(() => {
    if (!user && !hasInitialized && !selectedAddress && !ipLocation.isLoading && !ipLocation.data) {
      ipLocation.refetch();
    }
  }, [user, hasInitialized, selectedAddress, ipLocation]);

  // Prepopulate address with selected property location if user is logged in
  useEffect(() => {
    if (user && selectedPropertyLocation && !hasInitialized && !selectedAddress) {
      // Create a simplified Feature object from the property location
      const propertyAddress = selectedPropertyLocation.address_text || '';
      if (propertyAddress) {
        // Create a minimal Feature object that will work with the component
        const feature: Feature = {
          type: 'Feature',
          id: 'property-location',
          geometry: {
            type: 'Point',
            coordinates: [0, 0], // Default coordinates as we don't have them
          },
          properties: {
            mapbox_id: 'property-location',
            full_address: propertyAddress,
            place_formatted: propertyAddress,
            name: propertyAddress,
            name_preferred: propertyAddress,
            feature_type: 'address',
            coordinates: {
              accuracy: 'point',
              latitude: 0,
              longitude: 0,
              routable_points: [],
            },
            context: {
              address: { mapbox_id: '', name: '', address: '', street_name: '' },
              country: { mapbox_id: '', name: '', country_code: '', country_code_alpha_3: '' },
              district: { mapbox_id: '', name: '' },
              place: { mapbox_id: '', name: '' },
              postcode: { mapbox_id: '', name: '' },
              region: { mapbox_id: '', name: '', region_code: '', region_code_full: '' },
              street: { mapbox_id: '', name: '' },
            },
            match_code: {
              address_number: '',
              confidence: '',
              country: '',
              locality: '',
              place: '',
              postcode: '',
              region: '',
              street: '',
            },
          },
        };
        setSelectedAddress(feature);
        setHasInitialized(true);
      }
    }
  }, [user, selectedPropertyLocation, hasInitialized, selectedAddress]);

  const handleSearch = useCallback(async () => {
    // Clear any previous errors
    setErrorMessage(null);

    // Trim whitespace from search query
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery || !selectedAddress) {
      setErrorMessage('Please enter both a search query and an address');
      return;
    }

    // Prevent double-tap on mobile
    if (isSearching || searchVendorsMutation.isPending) {
      return;
    }

    setIsSearching(true);

    try {
      // Use full_address if available, otherwise fall back to place_formatted or name
      const addressToUse =
        selectedAddress.properties.full_address ||
        selectedAddress.properties.place_formatted ||
        selectedAddress.properties.name ||
        '';

      if (!addressToUse) {
        setErrorMessage('Please select a valid address from the dropdown');
        setIsSearching(false);
        return;
      }

      const result = await searchVendorsMutation.mutateAsync({
        address: addressToUse,
        query: trimmedQuery,
      });

      // Check if we actually got results
      if (!result) {
        throw new Error('No response from server');
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Add a small delay to show the animation before transitioning
      timeoutRef.current = setTimeout(() => {
        setIsSearching(false);
        // Redirect to appropriate route based on authentication status
        const targetRoute = user ? PMPRoutes.vendorSearchResults : PublicRoutes.vendorSearchResults;
        // Use replace instead of push to avoid back button issues on mobile
        history.replace(targetRoute, {
          vendors: result.vendors || [],
          searchQuery: trimmedQuery,
          address: addressToUse,
          disclaimer: result.disclaimer,
          search_query: result.search_query,
          searchId: result.searchId, // Pass the search tracking ID
        });
      }, 1500);
    } catch (error) {
      setIsSearching(false);

      // Set a more detailed error message
      if (error instanceof Error) {
        if (error.message.includes('Network') || error.message.includes('fetch')) {
          setErrorMessage('Network error. Please check your internet connection and try again.');
        } else {
          const msg =
            error.message || 'An error occurred while searching for providers. Please try again.';
          setErrorMessage(msg);
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    }
  }, [searchQuery, selectedAddress, searchVendorsMutation, user, history, isSearching]);

  // Handle incoming vendor search requests from Meet Terri calls
  useEffect(() => {
    if (lastSearchRequest && !isSearching) {
      // Set the search query from the Meet Terri call
      setSearchQuery(lastSearchRequest.search_query);

      // Create a Feature object for the address
      const addressFeature: Feature = {
        type: 'Feature',
        id: 'meet-terri-address',
        geometry: {
          type: 'Point',
          coordinates: [0, 0], // Will be resolved by the search
        },
        properties: {
          mapbox_id: 'meet-terri-address',
          full_address: lastSearchRequest.address,
          place_formatted: lastSearchRequest.address,
          name: lastSearchRequest.address,
          name_preferred: lastSearchRequest.address,
          feature_type: 'address',
          coordinates: {
            accuracy: 'point',
            latitude: 0,
            longitude: 0,
            routable_points: [],
          },
          context: {
            address: { mapbox_id: '', name: '', address: '', street_name: '' },
            country: { mapbox_id: '', name: '', country_code: '', country_code_alpha_3: '' },
            district: { mapbox_id: '', name: '' },
            place: { mapbox_id: '', name: '' },
            postcode: { mapbox_id: '', name: '' },
            region: { mapbox_id: '', name: '', region_code: '', region_code_full: '' },
            street: { mapbox_id: '', name: '' },
          },
          match_code: {
            address_number: '',
            confidence: '',
            country: '',
            locality: '',
            place: '',
            postcode: '',
            region: '',
            street: '',
          },
        },
      };

      setSelectedAddress(addressFeature);

      // Clear the request to prevent re-triggering
      clearLastRequest();

      // Automatically trigger the search after a short delay
      setTimeout(() => {
        handleSearch();
      }, 1000);
    }
  }, [lastSearchRequest, isSearching, clearLastRequest, handleSearch]);

  // Clean up timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    []
  );

  // const handleMeetTerriClick = () => {
  //   setShowMeetTerriDialog(true);
  //   setMeetTerriError(null);
  // };

  const handleMeetTerriCall = async (phoneNumber: string) => {
    try {
      const result = await initiateMeetTerriMutation.mutateAsync({
        phone_number: phoneNumber,
      });

      if (result.success) {
        // Store session token if provided (for anonymous users)
        if (result.session_token) {
          setMeetTerriSessionToken(result.session_token);
        }
        // Close dialog on success
        setShowMeetTerriDialog(false);
        setMeetTerriError(null);
        // Show success message
        setErrorMessage(null);
      } else {
        setMeetTerriError(result.message || 'Failed to initiate call. Please try again.');
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'An error occurred while initiating the call. Please try again.';
      setMeetTerriError(errorMsg);
    }
  };

  // const handleLoadRecentSearch = useCallback((query: string, address: string) => {
  //   // Set the search query
  //   setSearchQuery(query);

  //   // Create a Feature object for the address
  //   const addressFeature: Feature = {
  //     type: 'Feature',
  //     id: 'recent-search-address',
  //     geometry: {
  //       type: 'Point',
  //       coordinates: [0, 0], // Will be resolved by the search
  //     },
  //     properties: {
  //       mapbox_id: 'recent-search-address',
  //       full_address: address,
  //       place_formatted: address,
  //       name: address,
  //       name_preferred: address,
  //       feature_type: 'address',
  //       coordinates: {
  //         accuracy: 'point',
  //         latitude: 0,
  //         longitude: 0,
  //         routable_points: [],
  //       },
  //       context: {
  //         address: { mapbox_id: '', name: '', address: '', street_name: '' },
  //         country: { mapbox_id: '', name: '', country_code: '', country_code_alpha_3: '' },
  //         district: { mapbox_id: '', name: '' },
  //         place: { mapbox_id: '', name: '' },
  //         postcode: { mapbox_id: '', name: '' },
  //         region: { mapbox_id: '', name: '', region_code: '', region_code_full: '' },
  //         street: { mapbox_id: '', name: '' },
  //       },
  //       match_code: {
  //         address_number: '',
  //         confidence: '',
  //         country: '',
  //         locality: '',
  //         place: '',
  //         postcode: '',
  //         region: '',
  //         street: '',
  //       },
  //     },
  //   };

  //   setSelectedAddress(addressFeature);
  // }, []);

  return (
    <Box
      sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
        backgroundColor: '#F8FAFC',
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={8} alignItems="center">
          {/* No-Spam Hero Section */}
          <Stack spacing={6} alignItems="center" sx={{ maxWidth: '1000px', width: '100%' }}>
            {/* Main Heading */}
            <Stack spacing={3} alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                <ShieldTickIcon width={40} height={40} />
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    fontWeight: 800,
                    color: 'success.600',
                    textAlign: 'center',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Get Help For Your Home Without the Spam!
                </Typography>
              </Stack>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  fontWeight: 500,
                  color: 'text.secondary',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                Find the right provider for your home's needs — handled by AI, 💯 completely free.
                No more spam calls, emails, or pushy sales tactics!
              </Typography>
            </Stack>

            {/* Privacy Benefits */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'success.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PhoneXIcon width={24} height={24} />
                  </Box>
                  <Stack>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.700' }}>
                      No Spam Calls
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Your phone stays private
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'success.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Mail01Icon width={24} height={24} />
                  </Box>
                  <Stack>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.700' }}>
                      No Spam Emails
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Your inbox stays clean
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'success.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ZapFastIcon width={24} height={24} />
                  </Box>
                  <Stack>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.700' }}>
                      AI-Powered
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Fast & accurate matching
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Stack>

          <Stack spacing={2} alignItems="center" maxWidth="1000px">
            {/* Value Proposition */}
            <Box
              sx={{
                p: 4,
                backgroundColor: 'success.50',
                borderRadius: 3,
                maxWidth: '800px',
                width: '100%',
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'success.100',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  🎯 How It Works: 💯 Zero Hassle, Maximum Results
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  From quick fixes like hanging a light to full renovations, Terri finds trusted
                  providers, contacts them on your behalf, and gets their availability so you can
                  choose—saving you time, stress, and unwanted sales calls.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    label="💯 100% Free"
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 'auto',
                      py: 0.5,
                      px: 1,
                    }}
                  />
                  <Chip
                    label="🔒 Private & Secure"
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 'auto',
                      py: 0.5,
                      px: 1,
                    }}
                  />
                  <Chip
                    label="⚡ AI-Powered"
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 'auto',
                      py: 0.5,
                      px: 1,
                    }}
                  />
                  <Chip
                    label="📱 No Account Needed"
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 'auto',
                      py: 0.5,
                      px: 1,
                    }}
                  />
                </Box>
              </Stack>
            </Box>
            <Box sx={{ flex: 1, width: '100%', maxWidth: { xs: '100%', md: '500px' } }}>
              <HowWeRankVendors initialExpanded={false} />
            </Box>
          </Stack>

          {!isSearching ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: 3,
                width: '100%',
                maxWidth: '900px',
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease-in-out',
                },
              }}
            >
              <Stack spacing={4}>
                {/* Search Form Header */}
                <Stack spacing={2} alignItems="center">
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      textAlign: 'center',
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    🚀 Start Your Search
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textAlign="center"
                    sx={{
                      maxWidth: '600px',
                      lineHeight: 1.6,
                    }}
                  >
                    Tell us about your project and location. We'll find the perfect providers and
                    handle all the communication for you!
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    📝 Describe Your Project
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="What's your project or problem? Feel free to go into as much detail as you'd like. For example: 'I need to fix a leaky faucet in my kitchen' or 'Looking for someone to install a ceiling fan in my bedroom'"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    variant="outlined"
                    disabled={isSearching || searchVendorsMutation.isPending}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <SearchLgIcon
                            width={22}
                            height={22}
                            style={{
                              marginRight: 12,
                              color: '#667eea',
                              alignSelf: 'flex-start',
                              marginTop: 8,
                            }}
                          />
                        ),
                      },
                      htmlInput: {
                        'aria-label': 'Describe your project',
                        autoComplete: 'off',
                        autoCorrect: 'off',
                        autoCapitalize: 'off',
                        spellCheck: false,
                        style: { fontWeight: 500, fontSize: '1rem' },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        '&:hover': {
                          backgroundColor: '#f1f3f4',
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          borderColor: '#667eea',
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                        },
                      },
                    }}
                  />
                </Stack>
                <Stack spacing={2}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    📍 Your Location
                  </Typography>
                  <MapboxAddressAutocomplete
                    value={selectedAddress}
                    onChange={setSelectedAddress}
                    placeholder={
                      !user && ipLocation.isLoading
                        ? 'Detecting your location...'
                        : 'Enter your address to find providers near you'
                    }
                    disabled={isSearching || searchVendorsMutation.isPending}
                  />
                </Stack>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSearch}
                  disabled={
                    !searchQuery.trim() ||
                    !selectedAddress ||
                    isSearching ||
                    searchVendorsMutation.isPending
                  }
                  sx={{
                    py: 2,
                    borderRadius: 3,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 6px 25px rgba(102, 126, 234, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&.Mui-disabled': {
                      background: '#E5E7EB',
                      color: '#9CA3AF',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                >
                  🔍 Find My Perfect Providers - 💯 Free!
                </Button>

                {/* Privacy Reassurance */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'info.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'info.100',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    🔒 <strong>Privacy Guaranteed:</strong> We only share your address, if required.
                    Providers contact us, not you. No spam, no pushy sales calls!
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ) : (
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 3,
                width: '100%',
                maxWidth: '800px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Box sx={{ width: 200, height: 200 }}>
                <BaseLottie animationData={vendorSearchAnimation} loop autoplay />
              </Box>
              <Stack spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Deploying our AI agent to scout the perfect providers...
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Our digital detective is on the case! Sit tight while we match you with top
                  providers tailored to your needs.
                </Typography>
              </Stack>
            </Paper>
          )}

          {!isSearching && (searchVendorsMutation.isError || errorMessage) && (
            <Alert
              severity="error"
              sx={{ maxWidth: '800px', width: '100%' }}
              onClose={() => {
                setErrorMessage(null);
                searchVendorsMutation.reset();
              }}
            >
              {errorMessage || 'An error occurred while searching for providers. Please try again.'}
            </Alert>
          )}
        </Stack>
      </Container>

      {/* Meet Terri Dialog */}
      <MeetTerriDialog
        open={showMeetTerriDialog}
        onClose={() => {
          setShowMeetTerriDialog(false);
          setMeetTerriError(null);
        }}
        onCall={handleMeetTerriCall}
        isLoading={initiateMeetTerriMutation.isPending}
        error={meetTerriError}
      />
    </Box>
  );
};

export default VendorSearchSection;
